// A clipping mask's curves tessellated as Rive's renderer tessellates them: rive-spine's companion runtime
// (`runtime/riveCurves.ts`, copied 2026-10-13, unchanged but for this header; engine decision 75). No imports: the
// converter uses it too, for the polygon it writes (cut for scale 1), and riveClipping.ts for the polygon at the scale a
// skeleton is drawn at.
//
// Rive's renderer draws a filled path, a clip's too, one of two ways (draw.cpp, triangulation_controller.hpp, gpu.hpp):
//  - as a midpoint fan: each cubic in ⌈n⌉ even steps of t, n = √(3·d) by Wang's formula at kParametricPrecision 4 (d:
//    the cubic's larger second difference in device pixels), every segment within 1/4 px of the curve;
//  - by interior triangulation, when its bounds cover at least 512² device pixels and it has at most 256 verbs: each
//    cubic chopped evenly in t into ⌈n / 16⌉ outer patches (OuterCubicPatchSegmentSpan), each patch in its own ⌈√(3·d)⌉
//    even steps (so a quarter ellipse in two halves of 9 and 10, not 19 even steps: measured against Rive).
// (Rive also holds triangulation to 2 ms a frame and tunes which paths it admits by area per cost; a clip that holds
// still is admitted on its first frame and its triangulation cached.)

/** A clipping polygon Rive tessellates as it draws (the converter's `rive.clips` entry): contours of cubics, each a
 *  start point then three points per cubic (two handles, the end; the last ends at the start), in the clipping slot's
 *  bone space. A ring: the outer contour from the slit round, then the inner one in the polygon's order. */
export interface RiveCurveClip {
  ring: boolean;
  contours: number[][];
}

const TRIANGULATION_MIN_AREA = 512 * 512;
const TRIANGULATION_MAX_VERBS = 256;
const OUTER_CUBIC_PATCH_SEGMENTS = 16;
const MAX_PARAMETRIC_SEGMENTS = 1023;
const MAX_CURVE_SUBDIVISIONS = Math.ceil(MAX_PARAMETRIC_SEGMENTS / OUTER_CUBIC_PATCH_SEGMENTS);

/**
 * A curved clip's polygon as Rive tessellates it where its bone's world matrix is (a, b, c, d) (spine-core's: x' = a·x +
 * b·y) and the skeleton is drawn at `scale` device pixels per unit. A ring is cut open along a slit 0.01 wide beside the
 * contours' starts, as the converter joins it.
 */
export function riveClipPolygon(clip: RiveCurveClip, a: number, b: number, c: number, d: number, scale: number): Float32Array {
  // The path's bounds, all its points (handles too) in world space, and its verbs: a move, the cubics, a close each.
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  let verbs = 0;
  for (const contour of clip.contours) {
    verbs += (contour.length - 2) / 6 + 2;
    for (let k = 0; k + 1 < contour.length; k += 2) {
      const x = a * contour[k]! + b * contour[k + 1]!;
      const y = c * contour[k]! + d * contour[k + 1]!;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }
  }
  const area = (maxX - minX) * (maxY - minY) * scale * scale;
  const triangulated = area >= TRIANGULATION_MIN_AREA && verbs <= TRIANGULATION_MAX_VERBS;
  const points = clip.contours.map((contour) => {
    const out: number[] = [];
    for (let i = 0; i + 7 < contour.length; i += 6) {
      const [x0, y0, x1, y1, x2, y2, x3, y3] = contour.slice(i, i + 8) as [number, number, number, number, number, number, number, number];
      // Wang's formula on the device-space second differences, kParametricPrecision 4: n = √(3·max |v|, |w|). A piece
      // [t0, t1] of the cubic has second differences h²·((1 - t)·v + t·w) at its ends (h = t1 - t0; B'' is linear).
      const vx = x0 - 2 * x1 + x2, vy = y0 - 2 * y1 + y2, wx = x1 - 2 * x2 + x3, wy = y1 - 2 * y2 + y3;
      const len = (t: number, h: number): number => Math.hypot(a * ((1 - t) * vx + t * wx) + b * ((1 - t) * vy + t * wy), c * ((1 - t) * vx + t * wx) + d * ((1 - t) * vy + t * wy)) * h * h * scale;
      const wang = (t0: number, t1: number): number => Math.sqrt(3 * Math.max(len(t0, t1 - t0), len(t1, t1 - t0)));
      const pieces = triangulated ? Math.min(MAX_CURVE_SUBDIVISIONS, Math.max(1, Math.ceil(wang(0, 1) / OUTER_CUBIC_PATCH_SEGMENTS))) : 1;
      for (let j = 0; j < pieces; j++) {
        const t0 = j / pieces;
        const t1 = (j + 1) / pieces;
        const segments = Math.min(MAX_PARAMETRIC_SEGMENTS, Math.max(1, Math.ceil(wang(t0, t1))));
        for (let k = 0; k < segments; k++) {
          const t = t0 + ((t1 - t0) * k) / segments;
          const u = 1 - t;
          const b0 = u * u * u, b1 = 3 * u * u * t, b2 = 3 * u * t * t, b3 = t * t * t;
          out.push(b0 * x0 + b1 * x1 + b2 * x2 + b3 * x3, b0 * y0 + b1 * y1 + b2 * y2 + b3 * y3);
        }
      }
    }
    return out;
  });
  if (!clip.ring) return Float32Array.from(points[0]!);
  const [o, i] = points as [number[], number[]];
  // A point 0.01 from (x, y) toward (tx, ty).
  const toward = (x: number, y: number, tx: number, ty: number): number[] => {
    const l = Math.hypot(tx - x, ty - y);
    return [x + ((tx - x) / l) * 0.01, y + ((ty - y) / l) * 0.01];
  };
  return Float32Array.from([
    ...toward(o[0]!, o[1]!, o[2]!, o[3]!), // the outer start, a hair along its first edge
    ...o.slice(2), o[0]!, o[1]!, // round the outer contour to its start
    ...i, // across, and round the inner one
    ...toward(i[0]!, i[1]!, i.at(-2)!, i.at(-1)!), // a hair short of where it came in
  ]);
}
